<template>
  <div v-if="open" class="fb-backdrop" @click.self="close">
    <div class="fb" role="dialog" aria-modal="true">
      <header class="fb__header">
        <button
          type="button"
          class="fb__back"
          :aria-label="$t('common.dismiss')"
          @click="close"
        >
          <SfIcon name="chevron" :size="14" />
          <span>{{ $t("profile.title") }}</span>
        </button>
        <h2 class="fb__title">{{ $t("feedback.title") }}</h2>
        <span class="fb__header-spacer" aria-hidden="true"></span>
      </header>

      <div ref="scroll" class="fb__messages">
        <p v-if="loading" class="fb__placeholder">{{ $t("feedback.loading") }}</p>
        <ul v-else class="fb__list">
          <!-- Welcome admin bubble — mirrors FeedbackView.swift's
               `feedback_message` greeting at the top of the thread. -->
          <li class="fb__item fb__item--admin">
            <span class="fb__avatar" aria-hidden="true">
              <img :src="adminAvatarUrl" alt="" />
            </span>
            <div class="fb__bubble fb__bubble--admin">
              <p class="fb__text">{{ $t("feedback.welcome") }}</p>
            </div>
          </li>

          <li
            v-for="(m, idx) in messages"
            :key="m.id || idx"
            class="fb__item"
            :class="{ 'fb__item--user': m.isUser, 'fb__item--admin': !m.isUser }"
          >
            <span v-if="!m.isUser" class="fb__avatar" aria-hidden="true">
              <img :src="adminAvatarUrl" alt="" />
            </span>
            <div
              class="fb__bubble"
              :class="m.isUser ? 'fb__bubble--user' : 'fb__bubble--admin'"
            >
              <!-- Addresses are links, as FeedbackView.swift's `linkified`
                   draws them. Kept on one line: the text is pre-wrap. -->
              <p v-if="m.text" class="fb__text"><template v-for="(part, pi) in linkParts(m.text)" :key="pi"><a v-if="part.href" class="fb__link" :href="part.href" target="_blank" rel="noopener noreferrer">{{ part.text }}</a><template v-else>{{ part.text }}</template></template></p>
              <ul v-if="m.attachments && m.attachments.length > 0" class="fb__attachments">
                <li v-for="(a, ai) in m.attachments" :key="ai">
                  <a :href="a.url" target="_blank" rel="noopener">
                    <SfIcon name="paperclip" :size="13" />
                    {{ a.fileName }}
                  </a>
                </li>
              </ul>
              <span class="fb__time">{{ formatTime(m.timestamp) }}</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- Suggestion chips — only when the conversation is empty AND the
           composer is empty, exactly like NoteView.swift's three pill
           buttons above the input. -->
      <div
        v-if="messages.length === 0 && !draft && !loading"
        class="fb__suggestions"
      >
        <button
          v-for="(label, key) in suggestions"
          :key="key"
          type="button"
          class="fb__suggestion"
          @click="pickSuggestion(key)"
        >
          {{ label }}
        </button>
      </div>

      <form class="fb__composer" :class="{ 'fb__composer--expanded': isExpanded }" @submit.prevent="send">
        <textarea
          ref="input"
          v-model="draft"
          :placeholder="$t('feedback.placeholder')"
          rows="1"
          class="fb__input"
          :disabled="sending"
          @keydown.enter.exact.prevent="send"
          @keydown.enter.shift.exact="onShiftEnter"
        ></textarea>
        <button
          type="submit"
          class="fb__send"
          :disabled="!canSend"
          :aria-label="$t('feedback.send')"
        >
          <span v-if="sending" class="fb__send-spinner" aria-hidden="true"></span>
          <span v-else class="fb__send-arrow" aria-hidden="true">↑</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { authState } from "../stores/authStore";
import { showSnack } from "../stores/snackbarStore";
import { linkParts } from "../utils/linkify";
import SfIcon from "./SfIcon.vue";

const ADMIN_AVATAR_URL =
  "https://em-content.zobj.net/source/microsoft-teams/337/man-technologist_1f468-200d-1f4bb.png";

export default {
  name: "FeedbackChat",
  components: { SfIcon },
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return {
      messages: [],
      draft: "",
      loading: true,
      sending: false,
      unsub: null,
      adminAvatarUrl: ADMIN_AVATAR_URL,
    };
  },
  computed: {
    uid() {
      return authState.currentUser ? authState.currentUser.uid : null;
    },
    canSend() {
      return !this.sending && this.draft.trim().length > 0 && !!this.uid;
    },
    isExpanded() {
      return this.draft.length > 30 || this.draft.includes("\n");
    },
    suggestions() {
      return {
        deleteNote: this.$t("feedback.suggestions.deleteNote"),
        improvement: this.$t("feedback.suggestions.improvement"),
        issue: this.$t("feedback.suggestions.issue"),
      };
    },
    /**
     * Map of suggestion key → "long" text the iOS version inserts into the
     * composer when a chip is tapped, plus the auto-reply text that gets
     * posted as an admin bubble seconds later. Mirrors `suggestionText`
     * and the inline switch in FeedbackView.swift around line 938.
     */
    suggestionPayloads() {
      return {
        deleteNote: {
          send: this.$t("feedback.suggestionsLong.deleteNote"),
          reply: this.$t("feedback.autoReplies.deleteNote"),
        },
        improvement: {
          send: this.$t("feedback.suggestionsLong.improvement"),
          reply: this.$t("feedback.autoReplies.improvement"),
        },
        issue: {
          send: this.$t("feedback.suggestionsLong.issue"),
          reply: this.$t("feedback.autoReplies.issue"),
        },
      };
    },
  },
  watch: {
    open(v) {
      if (v) {
        this.attachListener();
        this.$nextTick(() => {
          this.scrollToBottom();
          if (this.$refs.input) this.$refs.input.focus();
        });
      } else {
        this.detachListener();
      }
    },
    messages() {
      this.$nextTick(() => this.scrollToBottom());
    },
    draft() {
      this.autosizeInput();
    },
  },
  beforeUnmount() {
    this.detachListener();
  },
  methods: {
    linkParts,
    close() {
      if (this.sending) return;
      this.$emit("close");
    },
    detachListener() {
      if (this.unsub) {
        this.unsub();
        this.unsub = null;
      }
    },
    attachListener() {
      this.detachListener();
      const uid = this.uid;
      if (!uid) return;
      this.loading = true;
      // Mirror FeedbackView.swift:1086 — order by timestamp desc, limit 30, then reverse client-side.
      const q = query(
        collection(db, "feedbacks", uid, "messages"),
        orderBy("timestamp", "desc"),
        limit(30),
      );
      this.unsub = onSnapshot(
        q,
        (snap) => {
          this.loading = false;
          const docs = snap.docs.map((d) => {
            const data = d.data() || {};
            const ts = data.timestamp;
            return {
              id: data.id || d.id,
              text: typeof data.text === "string" ? data.text : "",
              isUser: data.isUser === true,
              seen: data.seen,
              attachments: Array.isArray(data.attachments) ? data.attachments : [],
              timestamp:
                ts && typeof ts.toDate === "function" ? ts.toDate() : ts || new Date(),
            };
          });
          docs.sort((a, b) => a.timestamp - b.timestamp);
          this.messages = docs;

          // Mark unseen admin messages as seen (mirrors iOS).
          const unseen = snap.docs.filter((d) => {
            const data = d.data() || {};
            return data.isUser === false && data.seen === false;
          });
          for (const d of unseen) {
            updateDoc(d.ref, { seen: true }).catch(() => null);
          }
        },
        (err) => {
          console.error("feedback listener error:", err);
          this.loading = false;
        },
      );
    },
    scrollToBottom() {
      const el = this.$refs.scroll;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
    },
    autosizeInput() {
      const el = this.$refs.input;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 160) + "px";
    },
    pickSuggestion(key) {
      const payload = this.suggestionPayloads[key];
      if (!payload) return;
      this.draft = payload.send;
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus();
      });
    },
    /**
     * Posts the auto-reply admin message that mirrors FeedbackView.swift's
     * inline switch (case 0/1/2). Best-effort — failure is silent so we
     * don't bother the user about an auxiliary write.
     */
    async maybePostAutoReply(originalText) {
      const trimmed = (originalText || "").trim();
      const matchKey = Object.keys(this.suggestionPayloads).find((key) => {
        const send = (this.suggestionPayloads[key].send || "").trim();
        return send && send === trimmed;
      });
      if (!matchKey) return;
      const replyText = this.suggestionPayloads[matchKey].reply;
      const uid = this.uid;
      if (!replyText || !uid) return;
      try {
        const feedbackRef = doc(db, "feedbacks", uid);
        const msgRef = await addDoc(collection(db, "feedbacks", uid, "messages"), {
          text: replyText,
          isUser: false,
          seen: false,
          timestamp: serverTimestamp(),
        });
        await updateDoc(msgRef, { id: msgRef.id });
        await setDoc(
          feedbackRef,
          {
            lastMessageAt: serverTimestamp(),
            latestMessage: replyText,
            latestIsUser: false,
          },
          { merge: true },
        );
      } catch (err) {
        console.warn("auto-reply post failed:", err);
      }
    },
    async send() {
      if (!this.canSend) return;
      const uid = this.uid;
      const text = this.draft.trim();
      if (!text || !uid) return;

      this.sending = true;
      const optimistic = {
        id: "_pending_" + Date.now(),
        text,
        isUser: true,
        timestamp: new Date(),
        attachments: [],
      };
      this.messages = [...this.messages, optimistic];
      this.draft = "";

      try {
        const feedbackRef = doc(db, "feedbacks", uid);
        await setDoc(feedbackRef, {}, { merge: true });
        const msgRef = await addDoc(collection(db, "feedbacks", uid, "messages"), {
          text,
          isUser: true,
          timestamp: serverTimestamp(),
        });
        await updateDoc(msgRef, { id: msgRef.id });
        // Update the thread summary doc so the admin list view in iOS
        // surfaces this message at the top — same shape iOS writes.
        await setDoc(
          feedbackRef,
          {
            lastMessageAt: serverTimestamp(),
            latestMessage: text,
            latestIsUser: true,
            platform: "Web",
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
            isPremium: !!(authState.userData && authState.userData.premium),
            ...(authState.userData && authState.userData.name
              ? { name: authState.userData.name }
              : {}),
          },
          { merge: true },
        );
        // If the user picked one of the suggestion chips, surface a
        // pre-baked admin reply just like iOS does.
        this.maybePostAutoReply(text);
      } catch (err) {
        console.error("send feedback failed:", err);
        this.messages = this.messages.filter((m) => m !== optimistic);
        showSnack(this.$t("feedback.error"), { variant: "error" });
      } finally {
        this.sending = false;
        this.$nextTick(() => {
          this.scrollToBottom();
          if (this.$refs.input) this.$refs.input.focus();
        });
      }
    },
    onShiftEnter() {
      // Allow newline in textarea (the @keydown.enter.exact handler does the send;
      // shift+enter falls through to default textarea behavior).
    },
    formatTime(date) {
      try {
        return new Intl.DateTimeFormat(this.$i18n.locale || "en", {
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
      } catch (_) {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.fb-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
}

@media (min-width: 720px) {
  .fb-backdrop {
    align-items: center;
  }
}

.fb {
  width: min(620px, 100%);
  height: min(720px, calc(100vh - 28px));
  display: flex;
  flex-direction: column;
  background: var(--app-surface-strong);
  border: 1px solid var(--app-border);
  border-radius: 22px 22px 0 0;
  box-shadow: var(--app-shadow);
  overflow: hidden;
}

@media (min-width: 720px) {
  .fb {
    border-radius: 22px;
  }
}

.fb__header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--app-border);
}

.fb__back {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 4px;
  border: 0;
  background: transparent;
  color: #ffe20e;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
}

.fb__back:hover {
  background: rgba(255, 226, 14, 0.1);
}

.fb__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--app-heading);
  text-align: center;
  letter-spacing: -0.01em;
}

.fb__header-spacer {
  justify-self: end;
  width: 60px;
  height: 1px;
}

.fb__messages {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  background: color-mix(in srgb, var(--app-bg) 70%, transparent);
}

.fb__placeholder {
  margin: 24px 0;
  text-align: center;
  font-size: 13px;
  color: var(--app-text-muted);
}

.fb__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fb__item {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.fb__item--user {
  flex-direction: row-reverse;
}

.fb__item--admin {
  flex-direction: row;
}

/* Tech-emoji avatar attached to admin messages — same image URL as
   FeedbackView.swift uses (Microsoft Teams emoji set). */
.fb__avatar {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.fb__avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.fb__bubble {
  max-width: 78%;
  padding: 12px 15px;
  border-radius: 20px;
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fb__bubble--user {
  background: #2563eb;
  color: #ffffff;
  border-bottom-right-radius: 8px;
  min-width: 60px;
}

.fb__bubble--admin {
  background: color-mix(in srgb, var(--app-text) 10%, transparent);
  color: var(--app-text);
  border-bottom-left-radius: 8px;
}

:root[data-theme="dark"] .fb__bubble--admin {
  background: color-mix(in srgb, #ffffff 10%, transparent);
}

.fb__text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Underlined, white in my bubbles and blue in the other side's, as the
   phone draws them. */
.fb__link {
  color: #007aff;
  text-decoration: underline;
}

/* Only the other side's: the dark rule outranks a plain bubble rule, and
   a blue link on my blue bubble would not read. */
:root[data-theme="dark"] .fb__bubble--admin .fb__link {
  color: #0a84ff;
}

.fb__bubble--user .fb__link {
  color: #ffffff;
}

.fb__attachments {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 12px;
  display: grid;
  gap: 2px;
}

.fb__attachments a {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: inherit;
  text-decoration: underline;
  opacity: 0.85;
}

.fb__time {
  font-size: 10px;
  opacity: 0.65;
  text-align: right;
  letter-spacing: 0.04em;
}

.fb__bubble--user .fb__time {
  color: rgba(255, 255, 255, 0.85);
}

/* Suggestion chips — three rounded pills mirroring NoteView.swift's
   suggestion stack at lines 549-580 of FeedbackView.swift. Stacked
   vertically and aligned left like the source. */
.fb__suggestions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 0 16px 8px;
}

.fb__suggestion {
  text-align: left;
  padding: 11px 15px;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--app-text) 16%, transparent);
  background: color-mix(in srgb, var(--app-text) 6%, transparent);
  color: var(--app-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.fb__suggestion:hover {
  background: color-mix(in srgb, var(--app-text) 12%, transparent);
  border-color: color-mix(in srgb, var(--app-text) 28%, transparent);
}

.fb__composer {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding: 10px 14px 14px;
  background: var(--app-surface);
  border-top: 1px solid var(--app-border);
}

.fb__composer .fb__input {
  flex: 1;
  min-height: 40px;
  max-height: 160px;
  resize: none;
  padding: 10px 14px;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--app-text) 12%, transparent);
  background: color-mix(in srgb, var(--app-bg) 65%, transparent);
  color: var(--app-text);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  transition: border-radius 0.18s ease;
}

.fb__composer--expanded .fb__input {
  border-radius: 16px;
}

.fb__input:focus {
  outline: 2px solid rgba(255, 226, 14, 0.45);
  outline-offset: 0;
  border-color: rgba(255, 226, 14, 0.55);
}

/* Yellow circular send button — matches the iOS arrow.up Circle. */
.fb__send {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 0;
  background: rgba(255, 226, 14, 0.92);
  color: #1a1a1a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 4px;
  font-weight: 700;
}

.fb__send:disabled {
  background: color-mix(in srgb, var(--app-text) 22%, transparent);
  color: color-mix(in srgb, var(--app-text-muted) 80%, transparent);
  cursor: not-allowed;
}

.fb__send-arrow {
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
}

.fb__send-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: #1a1a1a;
  animation: fb-spin 0.8s linear infinite;
}

@keyframes fb-spin {
  to { transform: rotate(360deg); }
}
</style>
